import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import PropTypes from "prop-types";
import styled from "styled-components/macro";

import PageLayout from "../../components/PageLayout";
import Card from "../../components/Card";
import LoadingCard from "../../components/LoadingCard";
import ShareReferralLink, { RatedOnTrustpilot } from "../../components/ShareReferralLink";
import ScrollToTop from "../../components/utils/ScrollToTop";
import { reportReferralCodeLookupFailure } from "../../utils/sentryUtils";
import {
  CLIENT_REFERRAL_TERMS_URL,
  amountForCopy,
  referralEmailSubject,
  referralShareText,
} from "../../utils/referralUtils";

// The public page a client lands on from the referral emails: their link with
// the ways to pass it on. No login, so it shows nothing but the link. The
// amounts come from the referral code, never from here.
export function ShareReferralLinkCard({ referralUrl, discountAmount }) {
  const discount = amountForCopy(discountAmount);

  return (
    <Card title="Share your Juno link" removeTopPadding>
      <p>
        <RatedOnTrustpilot /> Anyone who signs up with your link gets {discount} off their conveyancing.
      </p>
      <ShareReferralLink
        referralUrl={referralUrl}
        shareText={referralShareText(discount)}
        emailSubject={referralEmailSubject(discount)}
        variant="refer_friend"
        utmSource="refer_a_friend_page"
      />
      <SmallPrint>
        <a href={CLIENT_REFERRAL_TERMS_URL} target="_blank" rel="noopener noreferrer">
          Terms apply
        </a>
      </SmallPrint>
    </Card>
  );
}

ShareReferralLinkCard.propTypes = {
  // The link the backend built for the code
  referralUrl: PropTypes.string.isRequired,
  // "£100.00" from the lookup; shown without whole-pound pence
  discountAmount: PropTypes.string.isRequired,
};

export function InvalidReferralLinkCard() {
  return (
    <Card title="This link isn’t valid" removeTopPadding>
      <p>
        <a href="https://www.juno.legal">Take me to www.juno.legal</a>
      </p>
    </Card>
  );
}

export function ReferralLinkLoadFailedCard() {
  return (
    <Card title="Sorry, we couldn’t load your link" removeTopPadding>
      <p>Please try again in a moment.</p>
    </Card>
  );
}

// The lookup response, or null for a code that cannot be shared from here:
// unknown, inactive, or owned by no client (only client codes come with a link).
const shareableCode = (referralData) =>
  referralData.referralCodeValid && referralData.referralUrl ? referralData : null;

function ReferAFriendPage() {
  const { referralCode } = useParams();
  const [state, setState] = useState({ loading: true, code: null, failed: false });

  useEffect(() => {
    let cancelled = false;
    // audience=owner tells the backend this is the client looking at their
    // own link, so it is not counted as a code view.
    fetch(`/api/referralCode/${referralCode}?audience=owner`)
      .then((resp) => {
        // an unknown code is a normal response, so a failed one is an error, not an invalid link
        if (!resp.ok) {
          const err = new Error(`Referral code lookup failed: ${resp.status}`);
          err.status = resp.status;
          throw err;
        }
        return resp.json();
      })
      .then(
        (referralData) => {
          if (!cancelled) setState({ loading: false, code: shareableCode(referralData), failed: false });
        },
        (err) => {
          reportReferralCodeLookupFailure(err);
          if (!cancelled) setState({ loading: false, code: null, failed: true });
        },
      );
    return () => {
      cancelled = true;
    };
  }, [referralCode]);

  const { loading, code, failed } = state;

  return (
    <PageLayout>
      <ScrollToTop />
      {loading && <LoadingCard />}
      {!loading && code && (
        <ShareReferralLinkCard referralUrl={code.referralUrl} discountAmount={code.discountAmount} />
      )}
      {!loading && failed && <ReferralLinkLoadFailedCard />}
      {!loading && !failed && !code && <InvalidReferralLinkCard />}
    </PageLayout>
  );
}

export default ReferAFriendPage;

const SmallPrint = styled.p`
  font-size: var(--juno-font-size-body-sm);
  color: var(--juno-color-text-secondary);
  margin-bottom: 0;

  a {
    color: inherit;
  }
`;
